import React, { useState, useEffect, useMemo, useCallback } from 'react';
import apiClient from '../api/client';

// ─── Метрики ─────────────────────────────────────────────────────────────────
const METRICS = [
  { key: 'd_orders', label: 'Заказы', short: 'Зак', hue: 142, fmt: (v) => Math.round(v) },
  { key: 'd_atbs', label: 'Корзины', short: 'Корз', hue: 213, fmt: (v) => Math.round(v) },
  { key: 'd_clicks', label: 'Клики', short: 'Кл', hue: 28, fmt: (v) => Math.round(v) },
  { key: 'd_views', label: 'Просмотры', short: 'Пр', hue: 260, fmt: (v) => Math.round(v) },
  { key: 'ctr', label: 'CTR (%)', short: 'CTR', hue: 189, fmt: (v) => `${v.toFixed(2)}%` },
  { key: 'cpc', label: 'CPC (₽)', short: 'CPC', hue: 38, fmt: (v) => `${v.toFixed(0)}₽` },
  { key: 'cpo', label: 'CPO (₽)', short: 'CPO', hue: 55, fmt: (v) => `${v.toFixed(0)}₽` },
  { key: 'drr', label: 'ДРР (%)', short: 'ДРР', hue: 320, fmt: (v) => `${v.toFixed(1)}%` },
  { key: 'd_sum_rub', label: 'Расход (₽)', short: 'Расх', hue: 4, fmt: (v) => `${v.toFixed(0)}₽` },
  { key: 'd_sum_price', label: 'Выручка (₽)', short: 'Выр', hue: 95, fmt: (v) => `${v.toFixed(0)}₽` },
];

const BASE_SUM_KEYS = ['d_views', 'd_clicks', 'd_orders', 'd_atbs', 'd_sum_rub', 'd_sum_price'];
const DERIVED_METRIC_KEYS = new Set(['ctr', 'cpc', 'cpo', 'drr']);

function sumHourMapBases(hourMap) {
  const t = { d_views: 0, d_clicks: 0, d_orders: 0, d_atbs: 0, d_sum_rub: 0, d_sum_price: 0 };
  if (!hourMap) return t;
  HOURS.forEach((h) => {
    const c = hourMap[h];
    if (!c) return;
    BASE_SUM_KEYS.forEach((k) => {
      t[k] += c[k] || 0;
    });
  });
  return t;
}

function sumTotalsBases(rows) {
  const t = { d_views: 0, d_clicks: 0, d_orders: 0, d_atbs: 0, d_sum_rub: 0, d_sum_price: 0 };
  rows.forEach((row) => {
    BASE_SUM_KEYS.forEach((k) => {
      t[k] += row[k] || 0;
    });
  });
  return t;
}

function derivedMetricFromTotals(t, metricKey) {
  switch (metricKey) {
    case 'ctr':
      return t.d_views > 0 ? (t.d_clicks / t.d_views) * 100 : 0;
    case 'cpc':
      return t.d_clicks > 0 ? t.d_sum_rub / t.d_clicks : 0;
    case 'cpo':
      return t.d_orders > 0 ? t.d_sum_rub / t.d_orders : 0;
    case 'drr':
      return t.d_sum_price > 0 ? (t.d_sum_rub / t.d_sum_price) * 100 : 0;
    default:
      return t[metricKey] || 0;
  }
}

function formatGrandTotal(metricKey, value) {
  if (value == null || (DERIVED_METRIC_KEYS.has(metricKey) && !Number.isFinite(value))) {
    return '—';
  }
  const m = METRICS.find((x) => x.key === metricKey);
  return m ? m.fmt(value) : String(value);
}

const DAYS_OPTIONS = [
  { value: 7,  label: '7 дней' },
  { value: 14, label: '14 дней' },
  { value: 30, label: '30 дней' },
  { value: 60, label: '60 дней' },
  { value: 90, label: '90 дней' },
];

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const ROW_LABEL_W = 72;
const CELL_W = 38;
const CELL_H = 34;
const DAY_TOTAL_W = 52;

/** Сумма (или производная метрика) по всем часам строки */
function computeRowDayTotal(hourMap, metricKey) {
  return derivedMetricFromTotals(sumHourMapBases(hourMap), metricKey);
}

// ─── Цвет ячейки ─────────────────────────────────────────────────────────────
function getCellStyle(value, maxValue, hue) {
  if (!value || !maxValue || maxValue === 0) {
    return { background: '#f3f4f6', color: '#d1d5db' };
  }
  const ratio = Math.min(value / maxValue, 1);
  const lightness = Math.round(95 - ratio * 72); // 95% (почти белый) → 23% (тёмный)
  const saturation = Math.round(50 + ratio * 30); // 50% → 80%
  const textColor = lightness < 58 ? '#ffffff' : '#1f2937';
  return {
    background: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
    color: textColor,
  };
}

// ─── Форматирование значения ──────────────────────────────────────────────────
function formatVal(value, metricKey) {
  const m = METRICS.find(m => m.key === metricKey);
  if (!m || !value) return '';
  return m.fmt(value);
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  const weekday = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'][d.getDay()];
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')} ${weekday}`;
}

// ─── Компонент тултипа ────────────────────────────────────────────────────────
function Tooltip({ cell, hour, rowLabel, anchorRef }) {
  if (!cell) return null;
  return (
    <div className="absolute z-50 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 w-44 pointer-events-none"
      style={{ bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 6 }}>
      <div className="font-semibold mb-1.5 text-center border-b border-white/20 pb-1">
        {rowLabel} / {hour}:00
      </div>
      <div className="space-y-0.5">
        {METRICS.map(m => (
          <div key={m.key} className="flex justify-between gap-2">
            <span className="text-gray-400">{m.label}</span>
            <span className="font-medium">{m.fmt(cell[m.key] || 0)}</span>
          </div>
        ))}
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 w-0 h-0"
        style={{ bottom: -5, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #111827' }} />
    </div>
  );
}

// ─── Компонент ячейки ─────────────────────────────────────────────────────────
function HeatCell({ cell, hour, rowLabel, metricKey, maxValue, hue, showValues }) {
  const [showTip, setShowTip] = useState(false);
  const value = cell ? (cell[metricKey] || 0) : 0;
  const style = getCellStyle(value, maxValue, hue);
  const displayVal = value > 0 && showValues ? formatVal(value, metricKey) : null;

  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: CELL_W, height: CELL_H }}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <div
        className="w-full h-full flex items-center justify-center text-xs font-medium rounded transition-all duration-150 cursor-default select-none"
        style={{ ...style, fontSize: displayVal && displayVal.length > 3 ? 9 : 10 }}
      >
        {displayVal}
      </div>
      {showTip && (
        <Tooltip cell={cell} hour={hour} rowLabel={rowLabel} />
      )}
    </div>
  );
}

// ─── Ячейка итога за день (колонка справа) ───────────────────────────────────
function DayTotalCell({ value, metricKey, maxValue, hue, showValues, bold = false }) {
  const style = getCellStyle(value, maxValue, hue);
  const displayVal = value > 0 && showValues ? formatVal(value, metricKey) : null;
  return (
    <div
      className="flex-shrink-0"
      style={{ width: DAY_TOTAL_W, height: CELL_H }}
    >
      <div
        className={`w-full h-full flex items-center justify-center rounded select-none ${
          bold ? 'text-xs font-bold' : 'text-xs font-medium'
        }`}
        style={{ ...style, fontSize: displayVal && displayVal.length > 4 ? 8 : 9 }}
      >
        {displayVal}
      </div>
    </div>
  );
}

// ─── Итоговая строка (сумма по часу) ─────────────────────────────────────────
function TotalsRow({ hourTotals, metricKey, hue, showValues, periodDayTotal, maxTotalsValue }) {
  const totalsMap = useMemo(() => {
    const m = {};
    hourTotals.forEach(t => { m[t.hour] = t; });
    return m;
  }, [hourTotals]);

  const maxValue = maxTotalsValue > 0 ? maxTotalsValue : 0;

  return (
    <div className="flex items-center gap-px mt-2 border-t border-gray-300 pt-2">
      <div
        className="flex-shrink-0 text-xs font-bold text-gray-700 bg-gray-100 rounded-lg flex items-center justify-center"
        style={{ width: ROW_LABEL_W, height: CELL_H }}
      >
        Итого
      </div>
      {HOURS.map(h => {
        const t = totalsMap[h];
        const value = t ? (t[metricKey] || 0) : 0;
        const style = getCellStyle(value, maxValue, hue);
        const displayVal = value > 0 && showValues ? formatVal(value, metricKey) : null;
        return (
          <div key={h} className="relative flex-shrink-0" style={{ width: CELL_W, height: CELL_H }}>
            <div
              className="w-full h-full flex items-center justify-center text-xs font-bold rounded"
              style={{ ...style, fontSize: displayVal && displayVal.length > 3 ? 9 : 10 }}
            >
              {displayVal}
            </div>
          </div>
        );
      })}
      <DayTotalCell
        value={periodDayTotal || 0}
        metricKey={metricKey}
        maxValue={maxValue}
        hue={hue}
        showValues={showValues}
        bold
      />
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function HeatmapPage({ clientId, embedded = false, defaultAdvertId = '' }) {
  const [cells, setCells] = useState([]);
  const [hourTotals, setHourTotals] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Фильтры
  const [metric, setMetric] = useState('d_orders');
  const [mode, setMode] = useState('dates');      // 'dates' | 'weekdays'
  const [days, setDays] = useState(30);
  const [advertId, setAdvertId] = useState(defaultAdvertId ? String(defaultAdvertId) : '');
  const [showValues, setShowValues] = useState(true);

  const loadData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const params = { client_id: clientId, days };
      if (advertId) params.advert_id = advertId;
      const { data } = await apiClient.get('/heatmap', { params });
      setCells(data.cells || []);
      setHourTotals(data.hourTotals || []);
      setCampaigns(data.campaigns || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [clientId, days, advertId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (defaultAdvertId) {
      setAdvertId(String(defaultAdvertId));
    }
  }, [defaultAdvertId]);

  // Текущая метрика
  const currentMetric = METRICS.find(m => m.key === metric) || METRICS[0];
  const { hue } = currentMetric;

  // ── Строим сетку по датам ──────────────────────────────────────────────────
  const dateRows = useMemo(() => {
    if (mode !== 'dates') return [];
    // Собираем уникальные даты
    const dateSet = new Set(cells.map(c => c.stat_date));
    const dates = [...dateSet].sort();

    return dates.map(date => {
      // map по часам
      const hourMap = {};
      cells.filter(c => c.stat_date === date).forEach(c => { hourMap[c.stat_hour] = c; });
      return { label: formatDate(date), key: date, hourMap };
    });
  }, [cells, mode]);

  // ── Строим сетку по дням недели ────────────────────────────────────────────
  const weekdayRows = useMemo(() => {
    if (mode !== 'weekdays') return [];
    // Группируем по dayOfWeek + hour
    const aggMap = {}; // key: `${dow}_${hour}`
    cells.forEach(c => {
      const dow = new Date(c.stat_date + 'T12:00:00').getDay(); // 0=Sun
      const key = `${dow}_${c.stat_hour}`;
      if (!aggMap[key]) aggMap[key] = { sums: {} };
      const agg = aggMap[key];
      BASE_SUM_KEYS.forEach((k) => {
        agg.sums[k] = (agg.sums[k] || 0) + (c[k] || 0);
      });
    });

    // Кол-во уникальных дат по дню недели (для подписи строки)
    const dateCounts = {}; // dow → кол-во уникальных дат
    cells.forEach(c => {
      const dow = new Date(c.stat_date + 'T12:00:00').getDay();
      if (!dateCounts[dow]) dateCounts[dow] = new Set();
      dateCounts[dow].add(c.stat_date);
    });

    // Строки: 1=Mon ... 6=Sat, 0=Sun (переупорядочим Пн-Вс)
    const dowOrder = [1, 2, 3, 4, 5, 6, 0];
    return dowOrder.map(dow => {
      const hourMap = {};
      HOURS.forEach(h => {
        const key = `${dow}_${h}`;
        const agg = aggMap[key];
        if (!agg) return;
        const cell = {};
        METRICS.forEach((m) => {
          cell[m.key] = derivedMetricFromTotals(agg.sums, m.key);
        });
        hourMap[h] = cell;
      });
      const dowLabel = WEEKDAY_NAMES[dow];
      const cnt = dateCounts[dow]?.size || 0;
      return { label: `${dowLabel} (${cnt}д)`, key: `dow_${dow}`, hourMap };
    });
  }, [cells, mode]);

  const rows = mode === 'dates' ? dateRows : weekdayRows;

  const rowDayTotals = useMemo(
    () => rows.map((row) => computeRowDayTotal(row.hourMap, metric)),
    [rows, metric],
  );

  // Максимум только по ячейкам сетки (час × день), без итогов
  const maxGridValue = useMemo(() => {
    let max = 0;
    rows.forEach((row) => {
      HOURS.forEach((h) => {
        const cell = row.hourMap[h];
        if (cell) max = Math.max(max, cell[metric] || 0);
      });
    });
    return max;
  }, [rows, metric]);

  // ── Общий итог по выбранной метрике ───────────────────────────────────────
  const periodDayTotal = useMemo(
    () => derivedMetricFromTotals(sumTotalsBases(hourTotals), metric),
    [hourTotals, metric],
  );

  // Максимум по итогам: колонка «За день», строка «Итого», угол
  const maxTotalsValue = useMemo(() => {
    let max = 0;
    rowDayTotals.forEach((v) => {
      max = Math.max(max, v || 0);
    });
    hourTotals.forEach((t) => {
      max = Math.max(max, t[metric] || 0);
    });
    max = Math.max(max, periodDayTotal || 0);
    return max;
  }, [rowDayTotals, hourTotals, metric, periodDayTotal]);

  const grandTotal = useMemo(
    () => formatGrandTotal(metric, periodDayTotal),
    [metric, periodDayTotal],
  );

  // ── Legend scale ──────────────────────────────────────────────────────────
  const legendSteps = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="flex flex-col items-start gap-4 w-full min-w-0">
      {/* ─── Заголовок ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {!embedded && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Тепловая карта</h2>
            <p className="text-sm text-gray-500 mt-0.5">Активность рекламных кампаний по часам</p>
          </div>
        )}
        <button
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 rounded-xl bg-indigo-500/80 text-white text-sm font-medium hover:bg-indigo-600/80 transition-colors disabled:opacity-50 backdrop-blur border border-white/30"
        >
          {loading ? 'Загрузка…' : '↺ Обновить'}
        </button>
      </div>

      {/* ─── Панель фильтров ────────────────────────────────────────────── */}
      <div className="glass-card rounded-2xl p-4">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Метрика */}
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">Метрика</label>
            <div className="flex flex-wrap gap-1.5">
              {METRICS.map(m => (
                <button
                  key={m.key}
                  onClick={() => setMetric(m.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    metric === m.key
                      ? 'bg-indigo-500 text-white border-indigo-600 shadow'
                      : 'bg-white/60 text-gray-700 border-gray-200 hover:bg-white/80'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Разделитель */}
          <div className="w-px h-8 bg-gray-200 self-end hidden sm:block" />

          {/* Режим строк */}
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">Строки</label>
            <div className="flex rounded-xl overflow-hidden border border-gray-200">
              <button
                onClick={() => setMode('dates')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  mode === 'dates' ? 'bg-indigo-500 text-white' : 'bg-white/70 text-gray-600 hover:bg-white'
                }`}
              >
                По датам
              </button>
              <button
                onClick={() => setMode('weekdays')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  mode === 'weekdays' ? 'bg-indigo-500 text-white' : 'bg-white/70 text-gray-600 hover:bg-white'
                }`}
              >
                По дням недели
              </button>
            </div>
          </div>

          {/* Период */}
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">Период</label>
            <div className="flex rounded-xl overflow-hidden border border-gray-200">
              {DAYS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDays(opt.value)}
                  className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    days === opt.value ? 'bg-indigo-500 text-white' : 'bg-white/70 text-gray-600 hover:bg-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Кампания */}
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">Кампания</label>
            <select
              value={advertId}
              onChange={e => setAdvertId(e.target.value)}
              className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 bg-white/70 text-gray-700 max-w-[200px] truncate"
            >
              <option value="">Все кампании</option>
              {campaigns.map(c => (
                <option key={c.advert_id} value={c.advert_id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Показывать значения */}
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                onClick={() => setShowValues(v => !v)}
                className={`w-9 h-5 rounded-full relative transition-colors ${showValues ? 'bg-indigo-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${showValues ? 'right-0.5' : 'left-0.5'}`} />
              </div>
              <span className="text-xs text-gray-600">Значения</span>
            </label>
          </div>
        </div>
      </div>

      {/* ─── Ошибка / пусто ─────────────────────────────────────────────── */}
      {error && (
        <div className="glass-card rounded-2xl p-4 text-red-600 text-sm">{error}</div>
      )}

      {!loading && !error && cells.length === 0 && (
        <div className="glass-card rounded-2xl p-10 text-center">
          <div className="text-4xl mb-3">📊</div>
          <p className="text-gray-500 font-medium">Данных пока нет</p>
          <p className="text-gray-400 text-sm mt-1">
            Скрипт сбора данных запускается каждый час в :05.<br />
            Первые данные появятся после следующего запуска.
          </p>
        </div>
      )}

      {/* ─── Тепловая карта ─────────────────────────────────────────────── */}
      {rows.length > 0 && (
        <div className="glass-card rounded-2xl p-4 w-max max-w-full self-start shrink-0 overflow-x-auto">
          {/* Шапка: метрика + итог */}
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap w-max min-w-0">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ background: `hsl(${hue}, 65%, 45%)` }} />
              <span className="text-sm font-semibold text-gray-700">{currentMetric.label}</span>
              {!loading && (
                <span className="text-xs text-gray-400">
                  · итого: <strong className="text-gray-700">{grandTotal}</strong>
                  · макс. ячейка: <strong className="text-gray-700">{currentMetric.fmt(maxGridValue)}</strong>
                  · макс. итог: <strong className="text-gray-700">{currentMetric.fmt(maxTotalsValue)}</strong>
                </span>
              )}
            </div>
            {/* Легенды: отдельно для карты и для итогов */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1" title="Шкала для ячеек карты">
                <span className="text-xs text-gray-500 mr-0.5">Карта</span>
                <span className="text-xs text-gray-400">мин</span>
                {legendSteps.map((step) => (
                  <div
                    key={`grid-${step}`}
                    className="rounded-sm"
                    style={{
                      width: 14,
                      height: 14,
                      background:
                        step === 0 ? '#f3f4f6' : `hsl(${hue}, ${50 + step * 30}%, ${95 - step * 72}%)`,
                    }}
                  />
                ))}
                <span className="text-xs text-gray-400">макс</span>
              </div>
              <div className="flex items-center gap-1" title="Шкала для итогов по дням и часам">
                <span className="text-xs text-gray-500 mr-0.5">Итоги</span>
                <span className="text-xs text-gray-400">мин</span>
                {legendSteps.map((step) => (
                  <div
                    key={`tot-${step}`}
                    className="rounded-sm ring-1 ring-gray-300/80"
                    style={{
                      width: 14,
                      height: 14,
                      background:
                        step === 0 ? '#f3f4f6' : `hsl(${hue}, ${50 + step * 30}%, ${95 - step * 72}%)`,
                    }}
                  />
                ))}
                <span className="text-xs text-gray-400">макс</span>
              </div>
            </div>
          </div>

          {/* Сетка: ширина = содержимое, подложка сжимается вместе с таблицей */}
          <div className="w-max">
              {/* Заголовок часов + колонка за день */}
              <div className="flex items-center gap-px mb-1">
                <div className="flex-shrink-0" style={{ width: ROW_LABEL_W }} />
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className="flex-shrink-0 text-center text-xs text-gray-400 font-medium"
                    style={{ width: CELL_W }}
                  >
                    {h}
                  </div>
                ))}
                <div
                  className="flex-shrink-0 text-center text-xs text-gray-500 font-semibold leading-tight px-0.5"
                  style={{ width: DAY_TOTAL_W }}
                  title="Сумма за день по всем часам"
                >
                  За день
                </div>
              </div>

              {/* Строки данных */}
              {loading ? (
                <div className="py-8 text-center w-max min-w-[200px]">
                  <div className="inline-block w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs text-gray-400 mt-2">Загрузка…</p>
                </div>
              ) : (
                rows.map((row, rowIdx) => (
                  <div key={row.key} className="flex items-center gap-px mb-px">
                    <div
                      className="flex-shrink-0 text-xs font-medium text-gray-600 text-right pr-2 leading-tight flex items-center justify-end"
                      style={{ width: ROW_LABEL_W, height: CELL_H }}
                    >
                      {row.label}
                    </div>
                    {HOURS.map((h) => (
                      <HeatCell
                        key={h}
                        cell={row.hourMap[h]}
                        hour={h}
                        rowLabel={row.label}
                        metricKey={metric}
                        maxValue={maxGridValue}
                        hue={hue}
                        showValues={showValues}
                      />
                    ))}
                    <DayTotalCell
                      value={rowDayTotals[rowIdx] || 0}
                      metricKey={metric}
                      maxValue={maxTotalsValue}
                      hue={hue}
                      showValues={showValues}
                      bold
                    />
                  </div>
                ))
              )}

              {!loading && hourTotals.length > 0 && (
                <TotalsRow
                  hourTotals={hourTotals}
                  metricKey={metric}
                  hue={hue}
                  showValues={showValues}
                  periodDayTotal={periodDayTotal}
                  maxTotalsValue={maxTotalsValue}
                />
              )}
          </div>
        </div>
      )}
    </div>
  );
}
